import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import JobWorkerSupportTypesComponent from "./../searchworkers/JobWorkerSupportTypesComponent";
import JobWorkerCareActivitiesComponent from "./../searchworkers/JobWorkerCareActivitiesComponent";
import SocialCommunityDetailsSupportComponent from "./SocialCommunityDetailsComponent";
import FrequencyDateTimeComponent from "./FrequencyDateTimeComponent";
import JobSupportDetailsComponent from "./JobSupportDetailsComponent";
import JobCreateReviewAndPostComponent from "./JobCreateReviewAndPostComponent";
import { GiCheckMark } from "react-icons/gi";
import { useUser } from "../../../context/UserContext";
import { useJobCreate } from "../../../context/JobCreateContext";
import {
  clearJobCreateIdHelper,
  validateSupportJobLocationDetails,
  validateFrequencyDetails,
  validateJobCraftDetails,
  parseTime,
  NO_PREFERENCE,
} from "../../../util_functions/helpers";
import { FaChevronLeft, FaChevronRight, FaPaperPlane } from "react-icons/fa";
import config from "../../../config/config";
import axios from "axios";

const WorkerStepsComponent = ({ onGoBackToSearch }) => {
  const {
    jobSelections,
    fetchJobSelectionsById,
    getJobCreateIdFromCookies,
    saveJobSelections,
    updateJobSelectionField,
  } = useJobCreate();
  const { clientUser, fetchLoggedInUser } = useUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [suburbValue, setSuburbValue] = useState(jobSelections.suburb || "");

  const [supportTypeDetailsState, setSupportTypeDetailsState] = useState({
    data: {},
    errors: [],
    isValid: false,
  });

  const [showSupportTypeDetailsErrors, setShowSupportTypeDetailsErrors] =
    useState(false);

  const [careActivityDetailsState, setCareActivityDetailsState] = useState({
    data: [],
    errors: [],
    isValid: false,
  });

  const [showCareActivityDetailsErrors, setShowCareActivityDetailsErrors] =
    useState(false);

  const [supportJobLocationState, setSupportJobLocationState] = useState({
    data: {},
    errors: [],
    isValid: false,
  });
  const [showSupportLocationErrors, setShowSupportLocationErrors] =
    useState(false);
  const [frequencyState, setFrequencyState] = useState({
    data: {},
    errors: [],
    isValid: false,
  });
  const [showFrequencyErrors, setShowFrequencyErrors] = useState(false);
  const [jobCraftState, setJobCraftState] = useState({
    data: {},
    errors: [],
    isValid: false,
  });
  const [showJobCraftErrors, setShowJobCraftErrors] = useState(false);
  const [isError, setIsError] = useState(false);
  const [jobPostedSuccess, setJobPostedSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const jobStatus = "open";

  const indicatorContainerRef = useRef(null);
  const stepRefs = useRef([]);

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  console.log(`[WorkerSteps] Render - currentStep=${currentStep}, maxStep=${maxStep}`);

  useEffect(() => {
    const stepSegment = location.pathname.match(
      /jobs\/new\/(standard|urgent)\/(step-(\d+))/
    );
    if (stepSegment) {
      const stepNum = parseInt(stepSegment[2].split("-")[1], 10);
      setCurrentStep(stepNum);
      setMaxStep(prev => {
        const newMax = Math.max(prev, stepNum);
        console.log(`[WorkerSteps] URL changed - stepNum=${stepNum}, maxStep updated from ${prev} to ${newMax}`);
        return newMax;
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const activeEl = stepRefs.current[currentStep - 1];
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    }
  }, [currentStep]);

  useEffect(() => {
    const onPop = () => {
      const stepSegment = location.pathname.match(
        /jobs\/new\/(standard|urgent)\/(step-(\d+))/
      );
      if (stepSegment) {
        const stepNum = parseInt(stepSegment[2].split("-")[1], 10);
        setCurrentStep(stepNum);
        setMaxStep(prev => {
          const newMax = Math.max(prev, stepNum);
          console.log(`[WorkerSteps] Popstate - stepNum=${stepNum}, maxStep updated from ${prev} to ${newMax}`);
          return newMax;
        });
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [location.pathname]);

  useEffect(() => {
    if (!clientUser) {
      fetchLoggedInUser()
        .then(() => {
          console.log("User fetched and set in the context");
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
        });
    }
  }, [clientUser, fetchLoggedInUser]);

  useEffect(() => {
    const fetchServerData = async () => {
      const jobCreateId = getJobCreateIdFromCookies();
      if (!jobCreateId) {
        navigate("/account/fs-dashboard/my-jobs/new/standard");
      } else {
        try {
          const fetchedSelections = await fetchJobSelectionsById(jobCreateId);
          console.log(
            "[JobWorkerSteps] server selections : ",
            fetchedSelections
          );

          setSuburbValue(fetchedSelections.suburbValue || "");

          const supportTypeDetails = fetchedSelections.supportTypeDetails || [];
          const typeDetailsErrors =
            supportTypeDetails.length > 0
              ? []
              : ["No support type(s) selected"];

          const newsupportTypeDetailsState = {
            data: supportTypeDetails,
            errors: typeDetailsErrors,
            isValid: typeDetailsErrors.length === 0,
          };

          console.log(
            "[fetchServerData] Setting supportTypeDetailsState:",
            newsupportTypeDetailsState
          );
          setSupportTypeDetailsState(newsupportTypeDetailsState);

          const careDetails = fetchedSelections.careActivityDetails || [];
          const careErrors =
            careDetails.length > 0 ? [] : ["No care activities selected"];
          const newCareActivityState = {
            data: careDetails,
            errors: careErrors,
            isValid: careErrors.length === 0,
          };

          console.log(
            "[fetchServerData] Setting careActivityDetailsState:",
            newCareActivityState
          );
          setCareActivityDetailsState(newCareActivityState);

          const supportJobLocationDetails = fetchedSelections.supportJobLocationDetails || {};

          const normalizedLocation = {
            supportJobLocation: supportJobLocationDetails.supportJobLocation ?? "",
            preferredQualities: supportJobLocationDetails.preferredQualities ?? "",
            preferredGender: supportJobLocationDetails.preferredGender ?? NO_PREFERENCE,
          };

          const locationErrors = validateSupportJobLocationDetails(normalizedLocation);

          const newLocationState = {
            data: normalizedLocation,
            errors: locationErrors,
            isValid: locationErrors.length === 0,
          };
          console.log(
            "[WorkerStepsComponent] setting supportJobLocationState:",
            newLocationState
          );
          setSupportJobLocationState(newLocationState);

          const freqDetails = fetchedSelections.frequencyDetails || {};
          const freqErrors = validateFrequencyDetails(freqDetails);
          const newFrequencyState = {
            data: freqDetails,
            errors: freqErrors,
            isValid: freqErrors.length === 0,
          };

          console.log(
            "[refreshServerData] setting frequencyState to:",
            newFrequencyState
          );

          setFrequencyState(newFrequencyState);

          const jobCraftDetails = fetchedSelections.jobCraftDetails || {};
          const jobCraftErrors = validateJobCraftDetails(jobCraftDetails);
          const newJobCraftState = {
            data: jobCraftDetails,
            errors: jobCraftErrors,
            isValid: jobCraftErrors.length === 0,
          };
          console.log(
            "[refreshServerData] setting jobCraftState to:",
            newJobCraftState
          );
          setJobCraftState(newJobCraftState);
        } catch (error) {
          console.error("Error fetching job selections:", error);
        }
      }
    };

    fetchServerData();
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById, navigate]);

  useEffect(() => {
    console.log("=== WorkerStepsComponent Render Debugging ===");

    console.log(`[Step 1] Always enabled`);

    console.log(`[Step 2] Checking supportTypeDetails...`);
    console.log("supportTypeDetails:", supportTypeDetailsState.data);
    console.log(
      "Step 2 Enabled:",
      supportTypeDetailsState.data !== null &&
      supportTypeDetailsState.data.length > 0
    );

    console.log(`[Step 3] Checking careActivityDetails...`);
    console.log("careActivityDetails:", careActivityDetailsState.data);
    console.log(
      "Step 3 Enabled:",
      isStepEnabled(2) && careActivityDetailsState.isValid
    );

    console.log(`[Step 4] Checking supportJobLocationState...`);
    console.log("supportJobLocationState:", supportJobLocationState);
    console.log(
      "Step 4 Enabled:",
      isStepEnabled(3) && supportJobLocationState.isValid
    );

    console.log(`[Step 5] Checking frequencyState...`);
    console.log("frequencyState:", frequencyState);
    console.log("Step 5 Enabled:", isStepEnabled(4) && frequencyState.isValid);

    console.log(`[Step 6] Checking jobCraftState...`);
    console.log("jobCraftState:", jobCraftState);
    console.log("Step 6 Enabled:", isStepEnabled(5) && jobCraftState.isValid);
  }, [
    supportTypeDetailsState,
    careActivityDetailsState,
    supportJobLocationState,
    frequencyState,
    jobCraftState,
  ]);

  const handleSupportTypesUpdate = (updatePayload) => {
    console.log(
      "[WorkerStepsComponent] handleSupportTypesUpdate:",
      updatePayload
    );

    console.log("📌 Support Type Details:", updatePayload.data);
    console.log("⚠️ Validation Errors:", updatePayload.errors);
    console.log("✅ Is Valid:", updatePayload.isValid);

    if (
      updatePayload &&
      updatePayload.data &&
      typeof updatePayload.isValid !== "undefined"
    ) {
      console.log(
        "[✔️ Validation Passed] Support Type Details Data Received Correctly!"
      );
    } else {
      console.error(
        "[❌ Validation Failed] Unexpected Support Type Details Data:",
        updatePayload
      );
    }

    setSupportTypeDetailsState(updatePayload);
  };

  const handleCareActivitiesUpdate = (updatePayload) => {
    console.log(
      "[WorkerStepsComponent] handleCareActivitiesUpdate:",
      updatePayload
    );

    const { data, errors, isValid } = updatePayload;

    console.log("📌 Care Activity Details:", data);
    console.log("⚠️ Validation Errors:", errors);
    console.log("✅ Is Valid:", isValid);

    setCareActivityDetailsState(updatePayload);
  };

  const handleSupportJobLocationUpdate = (updatePayload) => {
    console.log(
      "[WorkerStepsComponent] Received from SocialCommunityDetails:",
      updatePayload
    );

    const { data, errors, isValid } = updatePayload;
    console.log("📌 Updated Job Location Data:", data);
    console.log("⚠️ Validation Errors:", errors);
    console.log("✅ Is Valid:", isValid);

    setSupportJobLocationState(updatePayload);
  };

  const handleFrequencyUpdate = (updatePayload) => {
    console.log(
      "[WorkerStepsComponent] Received from FrequencyDatesTimesComponent:",
      updatePayload
    );

    const { data, errors, isValid } = updatePayload;
    console.log("📌 Updated Frequency Details:", data);
    console.log("⚠️ Validation Errors:", errors);
    console.log("✅ Is Valid:", isValid);

    if (
      updatePayload &&
      updatePayload.data &&
      typeof updatePayload.isValid !== "undefined"
    ) {
      console.log("[✔️ Validation Passed] Frequency Data Received Correctly!");
    } else {
      console.error(
        "[❌ Validation Failed] Unexpected Frequency Data:",
        updatePayload
      );
    }

    setFrequencyState(updatePayload);
  };

  const handleJobCraftStateUpdate = (updatePayload) => {
    console.log(
      "[WorkerStepsComponent] Received from JobcraftComponent:",
      updatePayload
    );
    const { data, errors, isValid } = updatePayload;
    console.log("📌 Updated JobCraft Details:", data);
    console.log("⚠️ Validation Errors:", errors);
    console.log("✅ Is Valid:", isValid);

    if (
      updatePayload &&
      updatePayload.data &&
      typeof updatePayload.isValid !== "undefined"
    ) {
      console.log("[✔️ Validation Passed] Job Craft Data Received Correctly!");
    } else {
      console.error(
        "[❌ Validation Failed] Unexpected Job Craft Data:",
        updatePayload
      );
    }

    setJobCraftState(updatePayload);
  };

  const handleBack = useCallback(() => {
    if (currentStep === 1) {
      onGoBackToSearch();
    } else {
      const newStep = currentStep - 1;
      setIsError(false);
      navigate(`${config.FS_ACCOUNT_ROUTE}/my-jobs/new/standard/step-${newStep}`, {
        state: { suburbValue },
      });
    }
  }, [currentStep, onGoBackToSearch, navigate]);

  const formatDate = (date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return mm + "/" + dd + "/" + yyyy + " " + hh + ":" + min;
  };

  const currentTimestamp = formatDate(new Date());

  const normalizeLocations = (arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Map();
    for (const raw of arr) {
      if (!raw) continue;
      const trimmed = String(raw).trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) seen.set(key, trimmed);
    }
    return Array.from(seen.values());
  };

  const normalizeFrequency = (fd = {}) => {
    const toNum = (v) =>
      v === "" || v == null ? null : (Number.isNaN(Number(v)) ? null : Number(v));

    const preferredDays = DAYS.reduce((acc, d) => {
      acc[d] = !!(fd.preferredDays?.[d]);
      return acc;
    }, {});

    const timeSlots = DAYS.reduce((acc, d) => {
      const arr = Array.isArray(fd.timeSlots?.[d]) ? fd.timeSlots[d] : [];
      acc[d] = arr
        .filter((s) => s && s.from && s.to && !s.error)
        .map((s) => ({ from: String(s.from), to: String(s.to) }));
      return acc;
    }, {});

    const payRateHourly = toNum(fd.payRateHourly);
    const payRateDaily = toNum(fd.payRateDaily);
    const payRateAnnual = toNum(fd.payRateAnnual);

    const cleanRange = (r = {}) => ({ min: toNum(r.min), max: toNum(r.max) });
    const payRanges = {
      hourly: cleanRange(fd.payRanges?.hourly),
      daily: cleanRange(fd.payRanges?.daily),
      annual: cleanRange(fd.payRanges?.annual),
    };

    const explicitType = ["hourly", "daily", "annual"].includes(fd.payType) ? fd.payType : "";
    let payType = explicitType;
    if (!payType) {
      const fixed = { hourly: payRateHourly, daily: payRateDaily, annual: payRateAnnual };
      const fixedUnits = Object.entries(fixed).filter(([, v]) => v != null);
      if (fixedUnits.length === 1) payType = fixedUnits[0][0];
      else {
        const rangeUnits = ["hourly", "daily", "annual"].filter(u => payRanges[u].min != null || payRanges[u].max != null);
        if (rangeUnits.length === 1) payType = rangeUnits[0];
      }
    }

    const sessionsPerWeek = toNum(fd.sessionsPerWeek);

    const sumMinutesFromSlots = () => {
      let mins = 0;
      for (const d of DAYS) {
        for (const s of (timeSlots[d] || [])) {
          const from = parseTime(s.from);
          const to = parseTime(s.to);
          if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
            mins += (to - from);
          }
        }
      }
      return mins;
    };

    const buildSessionRanges = () => {
      const existing = Array.isArray(fd.sessionRanges) ? fd.sessionRanges : null;
      const computed = [];

      for (const day of DAYS) {
        if (!preferredDays[day]) continue;
        for (const s of timeSlots[day] || []) {
          const fromMin = parseTime(s.from);
          const toMin = parseTime(s.to);
          if (Number.isFinite(fromMin) && Number.isFinite(toMin) && toMin > fromMin) {
            const minutes = toMin - fromMin;
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            const parts = [];
            if (h > 0) parts.push(`${h} hr${h === 1 ? "" : "s"}`);
            if (m > 0) parts.push(`${m} min${m === 1 ? "" : "s"}`);
            const label = parts.length ? parts.join(" ") : "0 mins";
            computed.push({ day, from: s.from, to: s.to, minutes, label });
          }
        }
      }

      return existing ?? computed;
    };

    const sessionRanges = buildSessionRanges();

    const hasAnySlots = sessionRanges.length > 0;
    let hoursPerSession = toNum(fd.hoursPerSession);
    let totalHoursPerWeek = null;

    if (String(fd.scheduling).toLowerCase() === "preferred") {
      hoursPerSession = null;
    }

    if (String(fd.supportType).toLowerCase() === "one-off") {
      totalHoursPerWeek = hoursPerSession;
    } else if (String(fd.scheduling).toLowerCase() === "preferred" && hasAnySlots) {
      const totalMins = sessionRanges.reduce((sum, r) => sum + (r.minutes || 0), 0);
      totalHoursPerWeek = +(totalMins / 60).toFixed(2);
    } else if (sessionsPerWeek != null && hoursPerSession != null) {
      totalHoursPerWeek = +(sessionsPerWeek * hoursPerSession).toFixed(2);
    }

    return {
      jobType: fd.jobType ?? "",
      shiftTypes: Array.isArray(fd.shiftTypes) ? fd.shiftTypes : [],

      supportType: fd.supportType ?? "",
      payType,
      payRateHourly,
      payRateDaily,
      payRateAnnual,
      payRanges,
      currency: fd.currency ?? "USD",

      sessionsPerWeek,
      hoursPerSession,

      supportStartDate: fd.supportStartDate ?? "",
      scheduling: fd.scheduling ?? "",
      additionalDetails: fd.additionalDetails ?? "",

      preferredDays,
      timeSlots,
      sessionRanges,
      totalHoursPerWeek,
    };
  };




  const selectedLocationsRaw = Array.isArray(jobSelections?.selectedLocations)
    ? jobSelections.selectedLocations.filter(Boolean)
    : [];
  const effectiveLocations = selectedLocationsRaw.length
    ? selectedLocationsRaw
    : (suburbValue ? [suburbValue] : []);
  const selectedLocations = normalizeLocations(effectiveLocations);

  const postedBy = [
    clientUser?.firstName ?? "",
    clientUser?.lastName?.charAt(0) ? clientUser.lastName.charAt(0) + "." : ""
  ].filter(Boolean).join(" ");

  const details = {
    postedBy,
    postedTime: currentTimestamp,
    selectedLocations,
    suburbValue: selectedLocations[0] || "",

    supportTypeDetails: supportTypeDetailsState.data,
    careActivityDetails: careActivityDetailsState.data,
    supportJobLocationDetails: supportJobLocationState.data,

    frequencyDetails: normalizeFrequency(frequencyState.data),

    jobCraftDetails: jobCraftState.data,
    jobStatus,
  };


  useEffect(() => {
    if (currentStep === 6) {
      console.log("[DEBUG] About to post job. 'details' object => ", details);
    }
  }, [currentStep, details]);

  const postJobDetails = async (details) => {
    try {
      console.log("Posting job details:", details);
      const response = await axios.post(`${config.API_BASE_URL}/v1/jobs`, {
        ...details,
        clientId: clientUser.id,
      });
      console.log("Job details posted successfully:", response.data);
      setJobPostedSuccess(true);
      const match = document.cookie.match(/jobCreateId=([^;]+)/);
      const jobCreateId = match ? match[1] : null;
      if (jobCreateId) {
        await clearJobCreateIdHelper(jobCreateId);
        console.log(`Cleared jobCreateId: ${jobCreateId}`);
      } else {
        console.log("No jobCreateId found in cookies to clear.");
      }
    } catch (error) {
      console.error("Error posting job details:", error);
    }
  };

  const handleNext = async () => {
    console.log("➡️ handleNext clicked on step:", currentStep);

    if (!validateStep()) {
      return;
    }

    try {
      switch (currentStep) {
        case 1:
          await updateJobSelectionField(
            "supportTypeDetails",
            supportTypeDetailsState.data
          );
          break;
        case 2:
          console.log(
            "[handleNext] Step 2 careActivityDetailsState:",
            careActivityDetailsState
          );
          await updateJobSelectionField(
            "careActivityDetails",
            careActivityDetailsState.data
          );
          break;
        case 3:
          console.log(
            "[handleNext] Step 3 supportJobLocationState:",
            supportJobLocationState
          );
          await updateJobSelectionField(
            "supportJobLocationDetails",
            supportJobLocationState.data
          );
          break;
        case 4:
          console.log("[handleNext] Step 4 frequencyState:", frequencyState);
          await updateJobSelectionField(
            "frequencyDetails",
            normalizeFrequency(frequencyState.data)
          );
          break;
        case 5:
          console.log("[handleNext] Step 5 jobCraftState:", jobCraftState);
          await updateJobSelectionField("jobCraftDetails", jobCraftState.data);
          break;
        case 6:
          await postJobDetails(details);
          break;
        default:
          console.error("Invalid step for updating job selections.");
      }

      if (currentStep < totalSteps) {
        const nextStep = currentStep + 1;
        navigate(
          `${config.FS_ACCOUNT_ROUTE}/my-jobs/new/standard/step-${nextStep}`,
          {
            state: { suburbValue },
          }
        );
        setCurrentStep(nextStep);
        setMaxStep(prev => {
          const newMax = Math.max(prev, nextStep);
          console.log(`[WorkerSteps] handleNext - moved to step ${nextStep}, maxStep updated from ${prev} to ${newMax}`);
          return newMax;
        });
      }
    } catch (error) {
      console.error("Failed to update job selections:", error);
    }
  };

  const validateStep = () => {
    let isValid = true;
    switch (currentStep) {
      case 1:
        console.log(
          "[ValidateStep] supportTypeDetailsState",
          supportTypeDetailsState
        );
        if (!supportTypeDetailsState.isValid) {
          setShowSupportTypeDetailsErrors(true);
          isValid = false;
        }
        break;
      case 2:
        console.log(
          "[ValidateStep] careActivityDetailsState",
          careActivityDetailsState
        );

        if (!careActivityDetailsState.isValid) {
          setShowCareActivityDetailsErrors(true);
          isValid = false;
        }
        break;
      case 3:
        console.log(
          "[ValidateStep] Support Job Location State",
          supportJobLocationState
        );
        if (!supportJobLocationState.isValid) {
          setShowSupportLocationErrors(true);
          isValid = false;
        }
        break;

      case 4:
        console.log("[ValidateStep] Frequency State", frequencyState);
        if (!frequencyState.isValid) {
          setShowFrequencyErrors(true);
          isValid = false;
        }

        break;

      case 5:
        if (!jobCraftState.isValid) {
          setShowJobCraftErrors(true);
          isValid = false;
        }

        break;

      case 6:
        return details !== null;
      default:
        return false;
    }
    return isValid;
  };

  const clearError = () => {
    setIsError(false);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <JobWorkerSupportTypesComponent
            onUpdate={handleSupportTypesUpdate}
            supportDetailsValue={supportTypeDetailsState.data}
            isSupportTypeDetailsError={
              showSupportTypeDetailsErrors && !supportTypeDetailsState.isValid
            }
            clearError={clearError}
          />
        );
      case 2:
        return (
          <JobWorkerCareActivitiesComponent
            onUpdate={handleCareActivitiesUpdate}
            selectedSupportTypes={supportTypeDetailsState.data}
            careActivityDetailsValue={careActivityDetailsState.data}
            isCareActivityDetailsError={
              showCareActivityDetailsErrors && !careActivityDetailsState.isValid
            }
            clearError={clearError}
          />
        );

      case 3:
        return (
          <SocialCommunityDetailsSupportComponent
            onUpdate={handleSupportJobLocationUpdate}
            isSupportLocationError={
              showSupportLocationErrors && !supportJobLocationState.isValid
            }
            clearError={clearError}
          />
        );
      case 4:
        console.log("Frequency details from step 4: ", frequencyState.data);
        return (
          <FrequencyDateTimeComponent
            onUpdate={handleFrequencyUpdate}
            isFrequencyError={showFrequencyErrors && !frequencyState.isValid}
            clearError={clearError}
          />
        );
      case 5:
        return (
          <JobSupportDetailsComponent
            onUpdate={handleJobCraftStateUpdate}
            isJobCraftError={showJobCraftErrors && !jobCraftState.isValid}
            clearError={clearError}
          />
        );
      case 6:
        return (
          <JobCreateReviewAndPostComponent
            user={clientUser}
            details={details}
            postJobDetails={postJobDetails}
            setCurrentStep={setCurrentStep}
            jobPostedSuccess={jobPostedSuccess}
            isError={isError}
            clearError={clearError}
          />
        );
      default:
        return null;
    }
  };

  const stepLabels = [
    "Care & Support Areas",
    "Support & Assistance Options",
    "Location & Gender Preferences",
    "Recurrence & Scheduling",
    "Job Details",
    "Review & Submit",
  ];

  const totalSteps = stepLabels.length;
  const percentage = Math.floor((maxStep / totalSteps) * 100);

  const isStepEnabled = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return true;

      case 2:
        return (
          supportTypeDetailsState.data !== null &&
          supportTypeDetailsState.data.length > 0
        );

      case 3:
        return isStepEnabled(2) && careActivityDetailsState.isValid;

      case 4:
        return isStepEnabled(3) && supportJobLocationState.isValid;

      case 5:
        return isStepEnabled(4) && frequencyState.isValid;

      case 6:
        return isStepEnabled(5) && jobCraftState.isValid;

      default:
        return false;
    }
  };

  const handleStepClick = (stepNumber) => {
    setCurrentStep(stepNumber);
    navigateStep(stepNumber);
  };

  const navigateStep = (stepNumber) => {
    navigate(
      `${config.FS_ACCOUNT_ROUTE}/my-jobs/new/standard/step-${stepNumber}`,
      {
        state: { suburbValue },
      }
    );
  };

  const hasStepData = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return Array.isArray(jobSelections.supportTypeDetails) && jobSelections.supportTypeDetails.length > 0;
      case 2:
        return Array.isArray(jobSelections.careActivityDetails) && jobSelections.careActivityDetails.length > 0;
      case 3:
        return jobSelections.supportJobLocationDetails && Boolean(jobSelections.supportJobLocationDetails.supportJobLocation);
      case 4:
        return jobSelections.frequencyDetails && Boolean(jobSelections.frequencyDetails.supportStartDate);
      case 5:
        const jc = jobSelections.jobCraftDetails;
        return !!(jc?.jobTitle?.trim() && jc?.jobDescription);
      case 6:
        return jobPostedSuccess;
      default:
        return false;
    }
  };

  const displayWidth = Math.max(percentage, 25);

  return (
    <div className="search-workers-container">
      <div className="progress-bar-container">
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${displayWidth}%` }}
          >
            <span className="progress-bar-text">
              {percentage}%
            </span>
          </div>
        </div>
      </div>
      <div className="job-step-indicators"
        ref={indicatorContainerRef}>
        {stepLabels.map((label, index) => {
          const refCallback = (el) => (stepRefs.current[index] = el);
          const stepNumber = index + 1;
          const isActive = currentStep === stepNumber;
          const isCompleted = hasStepData(stepNumber) || stepNumber < maxStep;
          const isCompletedActive = isCompleted && isActive;
          const stepIsEnabled = !jobPostedSuccess && (isCompleted || isStepEnabled(stepNumber));

          console.log(
            `[WorkerSteps] Step ${stepNumber}: isActive=${isActive}, isCompleted=${isCompleted}, isCompletedActive=${isCompletedActive}, enabled=${stepIsEnabled}`
          );

          const stepClass = [
            "job-step-indicator",
            isActive ? "active" : "",
            isCompleted ? "completed" : "",
            isCompletedActive ? "completed-active" : "",
            jobPostedSuccess ? "disabled-step-indicator" : "",
            !stepIsEnabled ? "job-step-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={index}
              ref={refCallback}
              className={stepClass}
              onClick={
                stepIsEnabled
                  ? () => handleStepClick(stepNumber)
                  : (e) => e.preventDefault()
              }
            >
              {isCompleted && <GiCheckMark className="step-completed-icon" />}
              {label}
            </div>
          );
        })}
      </div>


      {renderStepContent(currentStep)}
      <div className="support-worker-steps-actions">
        {currentStep >= 1 && <button className="swsa-btn" onClick={handleBack}>
          <FaChevronLeft aria-hidden="true" />
          <span>Go Back</span>
          </button>}
        {currentStep < 6 ? (
          <button className="swsa-btn" onClick={handleNext}>
            <span>Proceed</span>
            <FaChevronRight aria-hidden="true" />
          </button>
        ) : (
          <button className="swsa-btn" onClick={handleNext}>
            <FaPaperPlane aria-hidden="true" />
             <span>Submit</span>
            </button>
        )}
      </div>
    </div>
  );
};

export default WorkerStepsComponent;
